import React, { useEffect, useState, useRef } from 'react';

interface AnimatedCounterProps {
    start?: number;
    end: number;
    duration: number; // in seconds
    delay?: number; // in seconds, delay before starting the animation
    fontSize?: string;
    additionalStyles?: React.CSSProperties;
    scaleAnimation?: boolean;
}

const AnimatedCounter: React.FC<AnimatedCounterProps> = ({ 
    start = 0,
    end, 
    duration, 
    delay = 0,
    fontSize, 
    additionalStyles,
    scaleAnimation = true
}) => {
    const [count, setCount] = useState(start);
    const [isAnimating, setIsAnimating] = useState(false);
    const requestRef = useRef<number | undefined>(undefined);
    const startTimeRef = useRef<number | undefined>(undefined);
    const endValue = useRef(end);
    const startValue = useRef(start);
    const prevCountRef = useRef(count);
    const delayTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
    const isDelayedRef = useRef(false);
    
    useEffect(() => {
        const animate = (timestamp: number) => {
            if (!startTimeRef.current) startTimeRef.current = timestamp;
            
            const elapsed = (timestamp - startTimeRef.current) / 1000; // Convert to seconds
            const progress = Math.min(elapsed / duration, 1);
            
            // Easing function for slowing down towards the end (easeOutQuadratic)
            const easeOutProgress = 1 - Math.pow(1 - progress, 2);
            
            const range = endValue.current - startValue.current;
            const currentCount = Math.floor(startValue.current + (easeOutProgress * range));
            setCount(currentCount);
            
            if (progress < 1) {
                requestRef.current = requestAnimationFrame(animate);
            } else {
                setCount(endValue.current); // Ensure we end exactly at the target number
            }
        };

        endValue.current = end;
        startValue.current = start;
        setCount(start); // Set initial value to start
        
        // Clear any existing animation or delay
        if (requestRef.current) {
            cancelAnimationFrame(requestRef.current);
        }
        
        if (delayTimeoutRef.current) {
            clearTimeout(delayTimeoutRef.current);
        }
        
        // Handle delay logic
        if (delay > 0) {
            isDelayedRef.current = true;
            delayTimeoutRef.current = setTimeout(() => {
                startTimeRef.current = performance.now();
                requestRef.current = requestAnimationFrame(animate);
                isDelayedRef.current = false;
            }, delay * 1000);
        } else {
            startTimeRef.current = performance.now();
            requestRef.current = requestAnimationFrame(animate);
        }
        
        return () => {
            if (requestRef.current) {
                cancelAnimationFrame(requestRef.current);
            }
            if (delayTimeoutRef.current) {
                clearTimeout(delayTimeoutRef.current);
            }
        };
    }, [end, start, delay, duration]);

    // Trigger scale animation when count changes
    useEffect(() => {
        if (scaleAnimation && prevCountRef.current !== count) {
            setIsAnimating(true);
            const timer = setTimeout(() => {
                setIsAnimating(false);
            }, 300); // Match this with the CSS transition duration
            prevCountRef.current = count;
            
            return () => clearTimeout(timer);
        }
    }, [count, scaleAnimation]);

    const combinedStyles: React.CSSProperties = {
        fontSize: fontSize || 'inherit',
        transform: isAnimating ? 'scale(1.3)' : 'scale(1)',
        display: 'inline-block',
        transition: 'transform 0.3s ease-in-out',
        ...additionalStyles
    };

    return <span style={combinedStyles}>{count}</span>;
};

export default AnimatedCounter;